import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { MailOutline as MailIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import MarketingHeading from '../shared/MarketingHeading';
import {
  HOMEPAGE_PRODUCT_VIDEO,
  PRODUCT_VIDEOS_SECTION,
  type ProductVideo,
} from '../../constants/productVideos';

const VideoPlayer: React.FC<{ video: ProductVideo; large?: boolean }> = ({ video, large }) => {
  const hasYoutube = Boolean(video.youtubeId && video.youtubeId.trim());
  const hasMp4 = Boolean(video.mp4Src && video.mp4Src.trim());

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        pt: large ? { xs: '56.25%', md: '48%' } : '56.25%',
        bgcolor: 'action.hover',
      }}
    >
      {hasYoutube ? (
        <Box
          component="iframe"
          title={video.title}
          src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      ) : hasMp4 ? (
        <Box
          component="video"
          controls
          playsInline
          preload="metadata"
          poster={video.posterSrc}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            bgcolor: '#0B1F14',
          }}
        >
          <source src={video.mp4Src} type="video/mp4" />
        </Box>
      ) : null}
    </Box>
  );
};

const ProductVideosSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box component="section" id={PRODUCT_VIDEOS_SECTION.id} sx={{ mb: { xs: 3, md: 4 } }}>
      <Stack spacing={1} sx={{ mb: 2.5, maxWidth: 720 }}>
        <MarketingHeading level="h3">{PRODUCT_VIDEOS_SECTION.title}</MarketingHeading>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {PRODUCT_VIDEOS_SECTION.subtitle}
        </Typography>
      </Stack>

      <Card
        variant="outlined"
        data-testid="homepage-product-video"
        sx={{
          mb: 2.5,
          overflow: 'hidden',
          borderColor: alpha(theme.palette.primary.main, 0.35),
          bgcolor: isDark ? alpha(theme.palette.common.white, 0.02) : 'background.paper',
        }}
      >
        <VideoPlayer video={HOMEPAGE_PRODUCT_VIDEO} large />
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {HOMEPAGE_PRODUCT_VIDEO.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, maxWidth: 720 }}>
            {HOMEPAGE_PRODUCT_VIDEO.description}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mt: 2.5 }}>
        <Button
          component="a"
          href={`mailto:${PRODUCT_VIDEOS_SECTION.demoEmail}?subject=Carbon%20Intelligence%20demo`}
          variant="outlined"
          startIcon={<MailIcon />}
        >
          {PRODUCT_VIDEOS_SECTION.demoLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default ProductVideosSection;
